import { useEffect, useState } from 'react';
import { KeyRound, LockKeyhole, LogOut } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import {
  clearQuickPinUnlock,
  getQuickPinStatus,
  isQuickPinUnlocked,
  markQuickPinUnlocked,
  verifyQuickPin,
} from '../lib/quickPinApi';

export default function QuickPinGate({ children }) {
  const { user, signOut } = useAuth();
  const [gate, setGate] = useState({ loading: true, required: false, lockedUntil: null });
  const [pin, setPin] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!user || isQuickPinUnlocked(user.id)) {
        if (active) setGate({ loading: false, required: false, lockedUntil: null });
        return;
      }
      try {
        const status = await getQuickPinStatus();
        if (!active) return;
        setGate({
          loading: false,
          required: Boolean(status?.enabled),
          lockedUntil: status?.locked_until || null,
        });
      } catch (error) {
        console.warn('Quick PIN status unavailable:', error?.message || error);
        if (active) setGate({ loading: false, required: false, lockedUntil: null });
      }
    }
    load();
    return () => { active = false; };
  }, [user]);

  async function unlock(event) {
    event.preventDefault();
    if (!/^\d{4,6}$/.test(pin)) {
      setMessage('Enter your 4 to 6 digit PIN.');
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      const result = await verifyQuickPin(pin);
      if (result?.ok) {
        markQuickPinUnlocked(user.id);
        setGate({ loading: false, required: false, lockedUntil: null });
        setPin('');
      } else if (result?.locked) {
        setGate((current) => ({ ...current, lockedUntil: result.locked_until || null }));
        setMessage('Quick PIN is temporarily locked after several attempts. Use your password or try again later.');
      } else {
        setMessage(`That PIN did not match.${result?.attempts_remaining != null ? ` ${result.attempts_remaining} attempt(s) remaining.` : ''}`);
      }
    } catch (error) {
      setMessage(error?.message || 'Quick PIN could not be checked.');
    } finally {
      setSubmitting(false);
    }
  }

  async function usePassword() {
    clearQuickPinUnlock(user?.id);
    await signOut();
  }

  if (gate.loading) return <div className="loading-screen"><div className="loader" /><p>Checking this trusted device…</p></div>;
  if (!gate.required) return children;

  return (
    <section className="quick-pin-lock">
      <div className="quick-pin-lock__card">
        <span className="quick-pin-lock__icon"><LockKeyhole /></span>
        <span className="eyebrow">Trusted device quick access</span>
        <h1>Enter your Quick PIN</h1>
        <p>Use the 4 to 6 digit PIN saved for this browser. Your full password is still required on a new device or after your Supabase session expires.</p>
        <form onSubmit={unlock}>
          <label>
            <span>Quick PIN</span>
            <div className="input-icon"><KeyRound size={19} /><input autoFocus inputMode="numeric" pattern="[0-9]*" minLength="4" maxLength="6" type="password" autoComplete="one-time-code" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))} /></div>
          </label>
          <button className="button button--primary button--wide" disabled={submitting}>{submitting ? 'Checking…' : 'Unlock portal'}</button>
        </form>
        {message && <p className="form-message error">{message}</p>}
        {gate.lockedUntil && <p className="quick-pin-lock__note">Locked until {new Date(gate.lockedUntil).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}.</p>}
        <button type="button" className="button button--ghost button--wide" onClick={usePassword}><LogOut size={17} /> Use full password instead</button>
      </div>
    </section>
  );
}
